import axios from "axios";
import { toast } from "sonner";

// Lege variabele betekent: gebruik dezelfde host als de frontend en laat
// Nginx/de lokale preview `/api` doorsturen naar FastAPI.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    headers: { "Content-Type": "application/json" },
    withCredentials: true,
});

// Ondersteunt zowel kale API-lijsten als verpakte lijsten uit verschillende
// backend/proxy-versies, zonder records inhoudelijk te veranderen.
export function asList(data, preferredKeys = []) {
    if (Array.isArray(data)) return data;
    if (!data || typeof data !== "object") return [];
    for (const key of [...preferredKeys, "items", "results", "data"]) {
        if (Array.isArray(data[key])) return data[key];
    }
    return Object.values(data).find(Array.isArray) || [];
}

// Format detail from FastAPI errors (string OR array of {msg})
export function formatApiErrorDetail(detail) {
    if (detail == null) return "Er ging iets mis. Probeer het opnieuw.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

// Listeners that the AuthContext can hook into to react to 401s
const unauthorizedListeners = new Set();
export function onUnauthorized(cb) {
    unauthorizedListeners.add(cb);
    return () => unauthorizedListeners.delete(cb);
}

// Endpoints die GEEN globale 5xx-toast krijgen (worden lokaal in de pagina
// afgehandeld of veroorzaken te veel ruis). 401's worden sowieso uitgesloten.
const SILENT_PATHS = ["/auth/me", "/auth/login"];

// Debounce: voorkom dat dezelfde fout 10x achter elkaar getoond wordt
let _lastToastKey = "";
let _lastToastAt = 0;
function _showServerErrorToast(message) {
    const now = Date.now();
    if (message === _lastToastKey && now - _lastToastAt < 4000) return;
    _lastToastKey = message;
    _lastToastAt = now;
    toast.error(message);
}

api.interceptors.response.use(
    (resp) => resp,
    (error) => {
        const status = error.response?.status;
        const url = error.config?.url || "";
        const detail =
            error.response?.data?.detail ||
            error.response?.statusText ||
            error.message;
        const message = formatApiErrorDetail(detail);
        // eslint-disable-next-line no-console
        console.warn(
            `[api] ${error.config?.method?.toUpperCase()} ${url} → ${
                status || "network"
            }: ${message}`
        );
        const isSilent = SILENT_PATHS.some((p) => url.endsWith(p));
        // Globale toast bij 5xx of netwerkfouten (geen response = geen verbinding)
        if (!isSilent) {
            if (!error.response) {
                _showServerErrorToast(
                    "Geen verbinding met de server. Controleer je internet en probeer opnieuw."
                );
            } else if (status >= 500) {
                _showServerErrorToast(
                    message ||
                        "Er ging iets mis aan de serverzijde. Probeer het opnieuw."
                );
            }
        }
        // Fire unauthorized listeners EXCEPT for the /auth/me probe
        if (status === 401 && !url.endsWith("/auth/me") && !url.endsWith("/auth/login")) {
            unauthorizedListeners.forEach((cb) => {
                try {
                    cb();
                } catch (e) {
                    // Een listener mag andere listeners niet blokkeren — alleen loggen
                    console.warn("[api] unauthorized listener threw", e);
                }
            });
        }
        return Promise.reject(error);
    }
);

export const endpoints = {
    products: "/products",
    heightProfiles: "/height-profiles",
    customers: "/customers",
    orders: "/orders",
    seed: "/seed",
    stats: "/dashboard/stats",
};
